import React, { lazy, Suspense, useEffect, useState } from 'react';
import { TabSkeleton } from './shared/TabSkeleton';
import { ExtendedProject } from '@/types/project';

// Lazy load all tab components
const ProjectOverviewTabContent = lazy(() => 
  import('./ProjectOverviewTabContent').then(module => ({
    default: module.ProjectOverviewTabContent
  }))
);

// Import already extracted components
const CommunicationTab = lazy(() => 
  import('./CommunicationTab').then(module => ({
    default: module.CommunicationTab
  }))
);

const ProjectNotesTab = lazy(() => 
  import('./ProjectNotesTab')
);

const PartnerInfoTab = lazy(() => 
  import('./PartnerInfoTab').then(module => ({
    default: module.PartnerInfoTab
  }))
);

const SalesManagerInfoTab = lazy(() => 
  import('./SalesManagerInfoTab').then(module => ({
    default: module.SalesManagerInfoTab
  }))
);

const SalesManagerFinanceTab = lazy(() => 
  import('./SalesManagerFinanceTab').then(module => ({
    default: module.SalesManagerFinanceTab
  }))
);

const ProjectManagementTab = lazy(() => 
  import('./ProjectManagementTab').then(module => ({
    default: module.ProjectManagementTab
  }))
);

const SiteSurveyTab = lazy(() => 
  import('./SiteSurveyTab').then(module => ({
    default: module.SiteSurveyTab
  }))
);

const BookingConsultationTab = lazy(() => 
  import('./BookingConsultationTab').then(module => ({
    default: module.BookingConsultationTab
  }))
);

const RequestThryveDesignTab = lazy(() => 
  import('./RequestThryveDesignTab').then(module => ({
    default: module.RequestThryveDesignTab
  }))
);

const CommercialDesignRequestTab = lazy(() => 
  import('./CommercialDesignRequestTab').then(module => ({
    default: module.CommercialDesignRequestTab
  }))
);

const NewProjectFormTab = lazy(() => 
  import('./NewProjectFormTab').then(module => ({
    default: module.NewProjectFormTab
  }))
);

const WorkOrdersTab = lazy(() => 
  import('./WorkOrdersTab').then(module => ({
    default: module.WorkOrdersTab
  }))
);

const ContractsTab = lazy(() => 
  import('./ContractsTab')
);

const LenderTab = lazy(() => 
  import('./LenderTab')
);

const DocumentsTabV2 = lazy(() =>
  import('./DocumentsTabV2').then(module => ({
    default: module.DocumentsTabV2
  }))
);

const FinanceTab = lazy(() => 
  import('./FinanceTab').then(module => ({
    default: module.FinanceTab
  }))
);

const CommercialFinanceTab = lazy(() => 
  import('./CommercialFinanceTab').then(module => ({
    default: module.CommercialFinanceTab
  }))
);

const ActivityTab = lazy(() => 
  import('./ActivityTab').then(module => ({
    default: module.ActivityTab
  }))
);

const ProjectTasksTab = lazy(() =>
  import('./ProjectTasksTab').then(module => ({
    default: module.ProjectTasksTab
  }))
);

const EmailTab = lazy(() =>
  import('@/components/email/EmailTab').then(module => ({
    default: module.EmailTab
  }))
);

const ReportsTab = lazy(() =>
  import('@/components/reports/ReportsTab').then(module => ({
    default: module.ReportsTab
  }))
);

interface TabManagerProps {
  activeTab: string;
  tabLoaded: Record<string, boolean>;
  project: ExtendedProject | null;
  userRoleId?: number | null;
  [key: string]: any; // For passing through other props
}

export function TabManager({
  activeTab,
  tabLoaded,
  project,
  userRoleId,
  ...props
}: TabManagerProps) {
  const [preloadedTabs, setPreloadedTabs] = useState<Set<string>>(new Set());
  
  // Preload next likely tabs based on current tab
  useEffect(() => {
    const preloadStrategies: Record<string, string[]> = {
      'opportunity': ['communication', 'finance', 'documents'],
      'design': ['documents', 'finance'],
      'finance': ['contracts', 'documents'],
      'communication': ['emails', 'notes', 'timeline'],
      'emails': ['communication', 'notes'],
      'tasks': ['project-tasks', 'timeline']
    };
    
    const tabsToPreload = preloadStrategies[activeTab] || [];
    
    tabsToPreload.forEach((tab, index) => {
      if (!preloadedTabs.has(tab)) {
        setTimeout(() => {
          // Dynamically import the tab component
          switch(tab) {
            case 'communication':
              import('./CommunicationTab');
              break;
            case 'emails':
              import('@/components/email/EmailTab');
              break;
            case 'reports':
              import('@/components/reports/ReportsTab');
              break;
            case 'finance':
              import('./FinanceTab');
              import('./CommercialFinanceTab');
              break;
            case 'documents':
              import('./DocumentsTabV2');
              break;
            case 'notes':
              import('./ProjectNotesTab');
              break;
            case 'timeline':
              import('./ActivityTab');
              break;
            case 'contracts':
              import('./ContractsTab');
              break;
            case 'project-tasks':
              import('./ProjectTasksTab');
              break;
          }
          setPreloadedTabs(prev => new Set(prev).add(tab));
        }, 1500 + (index * 500)); // Stagger preloading
      }
    });
  }, [activeTab, preloadedTabs]);
  
  // Render the active tab with Suspense
  const renderTab = () => {
    if (!tabLoaded[activeTab]) {
      return <TabSkeleton />;
    }
    
    switch (activeTab) {
      case 'opportunity':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectOverviewTabContent
              project={project}
              projectId={props.projectId}
              formValues={props.formValues}
              canEditProject={props.canEditProject}
              loading={props.loading}
              handleInputChange={props.handleInputChange}
              handleSelectChange={props.handleSelectChange}
              handleArrayChange={props.handleArrayChange}
              setFormValues={props.setFormValues}
              handleProjectUpdate={props.handleProjectUpdate}
              handleClose={props.handleClose}
              handleDelete={props.handleDelete}
            />
          </Suspense>
        );
        
      case 'new-project-form':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <NewProjectFormTab 
              formData={props.formData}
              updateFormData={props.updateFormData}
              handleFileChange={props.handleFileChange}
              errors={props.errors}
              pipelineId={props.pipelineId}
            />
          </Suspense>
        );
        
      case 'design':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <DocumentsTabV2
              filePreviewUrls={props.filePreviewUrls}
              fileLoading={props.fileLoading}
              uploadStatus={props.uploadStatus}
              documents={props.documents}
              documentMetadata={props.documentMetadata}
              dragActive={props.dragActive}
              dlVerificationStatus={props.dlVerificationStatus}
              dlVerificationData={props.dlVerificationData}
              ubVerificationStatus={props.ubVerificationStatus}
              ubVerificationData={props.ubVerificationData}
              isVerifying={props.isVerifying}
              isVerifyingUb={props.isVerifyingUb}
              handleFileChange={props.handleFileChange}
              handleDrag={props.handleDrag}
              handleDrop={props.handleDrop}
              handleRemoveFile={props.handleRemoveFile}
              handleVerifyDriversLicense={props.handleVerifyDriversLicense}
              handleVerifyUtilityBill={props.handleVerifyUtilityBill}
              renderFilePreview={props.renderFilePreview}
              userRoleId={userRoleId}
              project={project}
              // Lazy loading props
              documentCounts={props.documentCounts}
              countsLoading={props.countsLoading}
              loadedFields={props.loadedFields}
              fetchFieldDocuments={props.fetchFieldDocuments}
            />
          </Suspense>
        );
        
      case 'request-design':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <RequestThryveDesignTab 
              formValues={props.formValues}
              handleInputChange={props.handleInputChange}
              onTaskSubmitted={props.onTaskSubmitted}
              isOpen={props.isOpen}
              onClose={props.onClose}
            />
          </Suspense>
        );
        
      case 'commercial-designs':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <CommercialDesignRequestTab 
              formValues={props.formValues}
              onTaskSubmitted={props.onTaskSubmitted}
              isOpen={props.isOpen}
            />
          </Suspense>
        );
        
      case 'book-consultation':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <BookingConsultationTab 
              projectId={props.projectId}
              project={project}
            />
          </Suspense>
        );
        
      case 'contracts':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ContractsTab project={project!} />
          </Suspense>
        );
        
      case 'lender':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <LenderTab project={project!} />
          </Suspense>
        );
        
      case 'site-survey':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <SiteSurveyTab project={project!} />
          </Suspense>
        );
        
      case 'tasks':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectManagementTab 
              project={project!}
              onProjectUpdate={props.onProjectUpdate}
            />
          </Suspense>
        );
        
      case 'finance':
        if (!project) return null;
        
        if (project.stages?.pipeline_id === 1 || project.stages?.pipeline_id === 57) {
          return (
            <Suspense fallback={<TabSkeleton />}>
              <CommercialFinanceTab 
                formValues={props.formValues}
                handleInputChange={props.handleInputChange}
              />
            </Suspense>
          );
        } else {
          return (
            <Suspense fallback={<TabSkeleton />}>
              <FinanceTab 
                project={project}
                currentUserId={props.currentUserId || ''}
                formValues={props.formValues}
                handleInputChange={props.handleInputChange}
                handleSelectChange={props.handleSelectChange}
                handleFileChange={props.handleFileChange}
                handleDrag={props.handleDrag}
                handleDrop={props.handleDrop}
                handleRemoveFile={props.handleRemoveFile}
                dragActive={props.dragActive}
                filePreviewUrls={props.filePreviewUrls}
                uploadStatus={props.uploadStatus}
              />
            </Suspense>
          );
        }
        
      case 'communication':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <CommunicationTab project={project!} defaultCommentTab={props.defaultCommentTab} />
          </Suspense>
        );

      case 'emails':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <EmailTab projectId={project?.id || ''} />
          </Suspense>
        );

      case 'reports':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ReportsTab
              projectId={project?.id || ''}
              pipelineId={project?.pipeline_id || ''}
            />
          </Suspense>
        );

      case 'notes':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectNotesTab project={project!} />
          </Suspense>
        );
        
      case 'partner':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <PartnerInfoTab 
              project={project!}
              onProjectUpdate={props.onProjectUpdate}
            />
          </Suspense>
        );
        
      case 'sales-manager':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <SalesManagerInfoTab 
              project={project!}
              onProjectUpdate={props.onProjectUpdate}
            />
          </Suspense>
        );
        
      case 'work-orders':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <WorkOrdersTab 
              project={project!}
              currentUserId={props.currentUserId || ''}
            />
          </Suspense>
        );
        
      case 'project-tasks':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectTasksTab 
              openTasks={props.openTasks}
              completedTasks={props.completedTasks}
              assigneeNames={props.assigneeNames}
              showCompletedTasks={props.showCompletedTasks}
              setShowCompletedTasks={props.setShowCompletedTasks}
              setShowTaskForm={props.setShowTaskForm}
              setSelectedTask={props.setSelectedTask}
              currentUserId={props.currentUserId}
            />
          </Suspense>
        );
        
      case 'timeline':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ActivityTab project={project!} />
          </Suspense>
        );
        
      case 'sales-manager-finance':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <SalesManagerFinanceTab 
              formValues={props.formValues}
              handleInputChange={props.handleInputChange}
            />
          </Suspense>
        );
        
      default:
        return null;
    }
  };
  
  return renderTab();
}